'use client';

import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';

type RadioGroupFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  required?: boolean;
  control: Control<T>;
  error?: string;
  colSpan?: 1 | 2;
  options: { value: string; label: string }[];
  appearance?: 'default' | 'plain';
};

export function RadioGroupField<T extends FieldValues>({
  name,
  label,
  required,
  control,
  error,
  options,
  appearance = 'default',
}: RadioGroupFieldProps<T>) {
  const isPlain = appearance === 'plain';

  return (
    <div className="flex flex-col gap-2">
      <span className="text-royal-500 text-xs font-semibold">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </span>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className={`flex flex-wrap ${isPlain ? 'gap-6' : 'gap-3'}`}>
            {options.map((opt) => {
              const checked = field.value === opt.value;
              return (
                <label
                  key={opt.value}
                  className={
                    isPlain
                      ? 'text-text-body flex cursor-pointer items-center gap-2 text-sm'
                      : `flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${
                          checked
                            ? 'border-royal-500 bg-royal-100 text-royal-500'
                            : 'hover:border-royal-300 border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`
                  }
                >
                  <input
                    type="radio"
                    className="sr-only"
                    value={opt.value}
                    checked={checked}
                    onChange={() => field.onChange(opt.value)}
                    onBlur={field.onBlur}
                  />
                  <span
                    className={`flex shrink-0 items-center justify-center rounded-full border-2 ${
                      isPlain ? 'size-4' : 'size-3.5'
                    } ${
                      checked
                        ? 'border-theme-primary'
                        : isPlain
                          ? 'border-gray-400'
                          : 'border-gray-300'
                    }`}
                  >
                    {checked && (
                      <span
                        className={`bg-theme-primary rounded-full ${
                          isPlain ? 'size-2' : 'size-1.5'
                        }`}
                      />
                    )}
                  </span>
                  {opt.label}
                </label>
              );
            })}
          </div>
        )}
      />
      {error && (
        <span className="mt-0.5 text-[10px] text-red-500">{error}</span>
      )}
    </div>
  );
}
